import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { createWorkOrder } from '#/api/momMgmt/workOrder/create';
import { fetchWorkOrderDetail } from '#/api/momMgmt/workOrder/fetchDetail';
import { updateWorkOrder } from '#/api/momMgmt/workOrder/update';
import { fetchProcessingFactorySelectOptions } from '#/api/momMgmt/settings/processingFactory/fetchSelectOptions';
import { cn } from '#/lib/utils';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import WorkOrderExpensesEditor from '#/modules/momMgmt/workOrder/components/WorkOrderExpensesEditor';
import WorkOrderBomSelectionsEditor from '#/modules/momMgmt/workOrder/components/WorkOrderBomSelectionsEditor';
import WorkOrderMaterialCandidatePickerDialog from '#/modules/momMgmt/workOrder/components/WorkOrderMaterialCandidatePickerDialog';
import WorkOrderStatusBadge from '#/modules/momMgmt/workOrder/components/WorkOrderStatusBadge';
import WorkOrderWarehouseFields from '#/modules/momMgmt/workOrder/components/WorkOrderWarehouseFields';
import {
  getWorkOrderApiErrorMessage,
  isWorkOrderVersionConflict,
} from '#/modules/momMgmt/workOrder/helper/apiError';
import {
  mapCandidateToFormPatch,
  mapWorkOrderDetailToFormValues,
} from '#/modules/momMgmt/workOrder/helper/formSerialize';
import {
  parseWorkOrderCreatePrefillParams,
  prefillWorkOrderFromSearchParams,
} from '#/modules/momMgmt/workOrder/helper/prefillWorkOrderFromSearchParams';
import {
  buildExpensesWritePayload,
  isExpensesChanged,
} from '#/modules/momMgmt/workOrder/helper/workOrderExpenses';
import { WORK_ORDER_STATUS } from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import {
  WORK_ORDER_FIELD_LABELS,
  resolveWorkOrderFieldLabel,
  workOrderFormSchema,
} from '#/modules/momMgmt/workOrder/schemas/workOrderSchema';
import {
  lookupQueryKey,
  workOrderDetailQueryKey,
  workOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';

const DEFAULT_VALUES = {
  scheduledStartDate: '',
  issueReason: '',
  remark: '',
  mtrId: '',
  mtrNo: '',
  mtrName: '',
  bomId: '',
  bomCode: '',
  bomName: '',
  bomVersion: '',
  bomStatus: '',
  processingFactoryId: '',
  workWarehouseId: '',
  targetWarehouseId: '',
  manufacturingQty: '',
  bomMaterialSelections: [],
  expenses: [],
};

const CreateEditWorkOrderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id);
  const updatedAtRef = useRef(null);
  const expensesRevisionRef = useRef(null);
  const originalExpensesRef = useRef([]);
  const skipClearOnFactoryChangeRef = useRef(false);
  const prefillStartedRef = useRef(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: workOrderDetailQueryKey(id),
    queryFn: () => fetchWorkOrderDetail(id),
    enabled: Boolean(id),
  });

  const factoryOptionsQuery = useQuery({
    queryKey: lookupQueryKey('processingFactorySelect'),
    queryFn: fetchProcessingFactorySelectOptions,
  });

  const warehouseOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const { isPending, save } = useCreateEditSave({
    id: isEditMode ? id : null,
    createFn: createWorkOrder,
    updateFn: updateWorkOrder,
    buildPayload: ({ expenses, ...values }) => {
      // 建立：有費用才送；草稿編輯：有變更才送（連同 expenses_revision）
      const shouldSendExpenses = isEditMode
        ? isExpensesChanged(expenses, originalExpensesRef.current)
        : (expenses ?? []).length > 0;
      return {
        ...values,
        ...(shouldSendExpenses ? {
          expenses: buildExpensesWritePayload(expenses),
          expensesRevision: isEditMode ? expensesRevisionRef.current : null,
        } : {}),
        updatedAt: updatedAtRef.current,
        status: detailQuery.data?.data?.status,
      };
    },
    listQueryKeyPrefix: workOrderListQueryKeyPrefix,
    detailQueryKey: workOrderDetailQueryKey,
    getEditPath: ROUTES.workOrderEdit,
    getDetailPath: ROUTES.workOrderDetail,
    getErrorMessage: (error) => getWorkOrderApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isWorkOrderVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(workOrderFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const bomId = useWatch({ control, name: 'bomId' });
  const mtrId = useWatch({ control, name: 'mtrId' });
  const mtrNo = useWatch({ control, name: 'mtrNo' });
  const mtrName = useWatch({ control, name: 'mtrName' });
  const bomName = useWatch({ control, name: 'bomName' });
  const bomVersion = useWatch({ control, name: 'bomVersion' });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    expensesRevisionRef.current = detail.expensesRevision ?? null;
    skipClearOnFactoryChangeRef.current = true;
    resetValidationSummary();
    const formValues = mapWorkOrderDetailToFormValues(detail);
    originalExpensesRef.current = formValues.expenses;
    reset(formValues);
  }, [detailQuery.data, reset, resetValidationSummary]);

  useEffect(() => {
    if (isEditMode || prefillStartedRef.current) {
      return undefined;
    }

    const { hasPrefill } = parseWorkOrderCreatePrefillParams(searchParams);
    if (!hasPrefill) {
      return undefined;
    }

    prefillStartedRef.current = true;
    let cancelled = false;

    prefillWorkOrderFromSearchParams({
      searchParams,
      setValue,
      navigate,
    }).catch(() => {
      if (cancelled) {
        return;
      }
      const fromWorkOrderId = searchParams.get('from_work_order_id')?.trim();
      if (fromWorkOrderId) {
        navigate(ROUTES.workOrderDetail(fromWorkOrderId), { replace: true });
        return;
      }
      navigate(ROUTES.workOrderCreate, { replace: true });
    });

    return () => {
      cancelled = true;
    };
  }, [isEditMode, navigate, searchParams, setValue]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  function handleCandidateConfirm(candidate) {
    const patch = mapCandidateToFormPatch(candidate);
    Object.entries(patch).forEach(([key, value]) => {
      setValue(key, value, { shouldDirty: true, shouldValidate: key === 'mtrId' });
    });
  }

  const detail = detailQuery.data?.data;
  const pageTitle = isEditMode ? '編輯工單' : '新增工單';
  const isPageLoading = isEditMode && detailQuery.isLoading;
  const isDetailNotFound = isEditMode && detailQuery.isError;
  const isProcessingEdit = isEditMode && detail?.status === WORK_ORDER_STATUS.PROCESSING;
  const isReadOnlyEdit = isEditMode
    && detail
    && detail.status !== WORK_ORDER_STATUS.DRAFT
    && detail.status !== WORK_ORDER_STATUS.PROCESSING;

  if (isEditMode && detail && isReadOnlyEdit) {
    return <Navigate to={ROUTES.workOrderDetail(id)} replace />;
  }

  const formDisabled = isPending;
  const fieldsDisabled = formDisabled || isProcessingEdit;
  const factoryOptions = factoryOptionsQuery.data ?? [];
  const warehouseOptions = warehouseOptionsQuery.data ?? [];

  return (
    <PageShell variant="formTall">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              {isEditMode && detail?.status ? (
                <WorkOrderStatusBadge status={detail.status} size="sm" />
              ) : null}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.workOrder}>返回列表</Link>
              </Button>
              {isEditMode ? (
                <Button type="button" variant="outline" asChild>
                  <Link to={ROUTES.workOrderDetail(id)}>返回詳情</Link>
                </Button>
              ) : null}
            </div>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getWorkOrderApiErrorMessage(detailQuery.error, '找不到工單資料')}
            </p>
          ) : null}

          {!isPageLoading && !isDetailNotFound ? (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={WORK_ORDER_FIELD_LABELS}
                fieldLabelResolver={resolveWorkOrderFieldLabel}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">單據資訊</h2>
                      {isProcessingEdit ? (
                        <p className="form-block__description">
                          生產中僅可改排程日；報廢／取消／替代料請走異動單。
                        </p>
                      ) : null}
                    </div>
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingDateInput
                        id="scheduledStartDate"
                        label="排程日"
                        control={control}
                        errors={errors}
                        disabled={formDisabled}
                      />
                      <FloatingInput
                        id="manufacturingQty"
                        label="製造量"
                        type="number"
                        register={register}
                        errors={errors}
                        disabled={fieldsDisabled}
                      />
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 @4xl/sheet:grid-cols-2">
                      <FloatingTextarea
                        id="issueReason"
                        label="事由"
                        register={register}
                        errors={errors}
                        rows={2}
                        disabled={fieldsDisabled}
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                        rows={2}
                        disabled={fieldsDisabled}
                      />
                    </div>
                  </section>

                  <section className="pt-2.5 pb-5">
                    <div className="mb-2 flex items-center gap-2">
                      <h2 className="form-block__title mb-0!">成品</h2>
                      <Button
                        type="button"
                        size="sm"
                        disabled={fieldsDisabled}
                        onClick={() => setPickerOpen(true)}
                      >
                        選擇成品
                      </Button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <p className="shrink-0 text-slate-800">BOM：</p>
                        <div
                          className={cn(
                            'min-w-0 rounded-md border border-slate-200 bg-slate-50',
                            'px-2 py-2',
                          )}
                        >
                          <p className="truncate text-sm font-medium text-slate-800">
                            {bomId
                              ? [bomName || '—', bomVersion ? `(v${bomVersion})` : ''].filter(Boolean).join(' ')
                              : '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="shrink-0 text-slate-800">物料：</p>
                        <div
                          className={cn(
                            'min-w-0 rounded-md border border-slate-200 bg-slate-50',
                            'px-2 py-2',
                          )}
                        >
                          {mtrId ? (
                            <>
                              <p className="truncate text-sm font-medium text-slate-800">
                                {mtrName || '—'}
                              </p>
                              <p className="truncate text-xs text-muted-foreground">
                                {mtrNo || mtrId}
                              </p>
                            </>
                          ) : (
                            <p className="text-sm text-muted-foreground">尚未選擇</p>
                          )}
                        </div>
                      </div>
                      {errors.mtrId ? (
                        <p className="text-destructive text-xs">
                          {errors.mtrId.message}
                        </p>
                      ) : null}
                    </div>
                    <div
                      className={cn(
                        'mt-4 grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-3',
                      )}
                    >
                      <WorkOrderWarehouseFields
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        clearErrors={clearErrors}
                        factoryOptions={factoryOptions}
                        warehouseOptions={warehouseOptions}
                        isFactoryLoading={factoryOptionsQuery.isLoading}
                        isWarehouseLoading={warehouseOptionsQuery.isLoading}
                        disabled={fieldsDisabled}
                        skipClearOnFactoryChangeRef={skipClearOnFactoryChangeRef}
                      />
                    </div>
                    <WorkOrderBomSelectionsEditor
                      control={control}
                      errors={errors}
                      setValue={setValue}
                      getValues={getValues}
                      bomId={bomId || null}
                      disabled={fieldsDisabled}
                    />
                  </section>

                  <section className="pt-2.5 pb-5">
                    <WorkOrderExpensesEditor
                      control={control}
                      register={register}
                      errors={errors}
                      disabled={fieldsDisabled}
                      description={isProcessingEdit
                        ? '生產中的費用請透過異動單新增或修改。'
                        : '已建立的費用不可移除；不再收取請把金額改為 0。'}
                    />
                  </section>
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.workOrder}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>

              <WorkOrderMaterialCandidatePickerDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                onConfirm={handleCandidateConfirm}
                selectedMtrId={mtrId}
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditWorkOrderPage;
